'use client';

import type { CreateOrderFormValues } from '@eliadmin/core/src/pages/cart/page';

import { CartSummaryCard } from '@eliadmin/core/src/components/cart/cart-summary-card';
import { CartOrderForm } from '@eliadmin/core/src/components/cart/order/cart-order-form';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { CartStep } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Suspense } from 'react';
import { useFormContext } from 'react-hook-form';
import { CartSection } from '../cart-section';
import { CartNavigationButton } from '../continue-button';
import { SummaryCardItem } from '../order-shipment/summary-card-item';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { Heading } from '../../ui/heading';

export function CartOrder() {
  const { t } = useTranslations();

  const { data: cart } = useCart();

  const form = useFormContext<CreateOrderFormValues>();

  // Validate on click instead of disabling the button: a disabled button never reveals which
  // field is wrong, because the form runs in `onSubmit` mode and no error is rendered before it.
  const validateOrderForm = () => form.trigger(undefined, { shouldFocus: true });

  return (
    <>
      <CartSection title={t('text3738')}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-8 md:flex-row md:justify-between lg:gap-12',
          )}
        >
          <div className="w-full md:max-w-[807px]">
            <Suspense fallback={<GenericLoader />}>
              <CartOrderForm />
            </Suspense>
          </div>
          <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[4px] border border-border bg-transparent p-3 sm:p-4 md:max-w-[569px] lg:p-6">
            <Heading title={t('text2138')} tag="h4" />
            {cart.items?.map(item => (
              <SummaryCardItem key={item.id} item={item} />
            ))}
            <Separator />
            <CartSummaryCard
              className={cn('border-none bg-transparent p-0 sm:p-0 md:p-0 lg:p-0')}
            />
          </div>
        </div>
        <div
          className={cn(
            'custom-container',
            'flex flex-col-reverse items-center justify-end gap-3 sm:flex-row sm:justify-end sm:pr-0',
          )}
        >
          <CartNavigationButton
            step={CartStep.PAYMENT_SHIPMENT}
            title={t('text1026')}
            variant={'secondary'}
            className="w-full whitespace-nowrap sm:w-fit"
            beforeIcon
          />
          <CartNavigationButton
            step={CartStep.SUMMARY}
            onBeforeContinue={validateOrderForm}
            className="w-full whitespace-nowrap sm:w-fit"
          />
        </div>
      </CartSection>
    </>
  );
}
